/**
 * PATH: apps/qa/src/lib/offline-db/types.ts
 * PURPOSE: IndexedDB schema types and database initialization for offline storage
 */

import { openDB, deleteDB, type DBSchema, type IDBPDatabase } from 'idb'

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface SyncOperation {
  id: string
  operationType: 'create' | 'update' | 'delete'
  entityType: 'inspection' | 'area' | 'item_instance' | 'evidence' | 'hold_point' | 'ncr'
  entityId: string
  payload: unknown
  createdAt: number
  attempts: number
  lastAttempt?: number
  error?: string
  status: 'pending' | 'in_progress' | 'failed' | 'synced'
  /** Session that queued the op; required before flush (P6.4 Option A). */
  ownerUserId?: string
  ownerOrganisationId?: string
}

export interface CachedInspection {
  id: string
  data: unknown
  lastSynced: number
  isOfflineCreated: boolean
}

export interface CachedArea {
  id: string
  inspectionId: string
  data: unknown
  items: unknown[]
  lastSynced: number
}

export interface CachedEvidence {
  id: string
  areaId: string
  itemInstanceId?: string
  blob: Blob
  metadata: {
    type: 'photo' | 'video' | 'document'
    timestamp: string
    gps?: { lat: number; lng: number }
    description?: string
  }
  status: 'pending' | 'uploading' | 'uploaded' | 'failed'
  uploadedAssetId?: string
  createdAt: number
}

export interface SyncMetadata {
  key: string
  value: unknown
}

export interface QAOfflineDB extends DBSchema {
  inspections: { key: string; value: CachedInspection; indexes: { 'by-synced': number } }
  areas: { key: string; value: CachedArea; indexes: { 'by-inspection': string; 'by-synced': number } }
  evidence: { key: string; value: CachedEvidence; indexes: { 'by-area': string; 'by-status': string; 'by-created': number } }
  syncQueue: { key: string; value: SyncOperation; indexes: { 'by-status': string; 'by-created': number; 'by-entity': string } }
  metadata: { key: string; value: SyncMetadata }
}

// ─────────────────────────────────────────────────────────────────────────────
// Database Initialization
// ─────────────────────────────────────────────────────────────────────────────

const DB_NAME = 'quotech-qa-offline'
const DB_VERSION = 1
let dbPromise: Promise<IDBPDatabase<QAOfflineDB>> | null = null

export function getDB(): Promise<IDBPDatabase<QAOfflineDB>> {
  if (!dbPromise) dbPromise = openOfflineDB()
  return dbPromise
}

async function openOfflineDB(isRetry = false): Promise<IDBPDatabase<QAOfflineDB>> {
  try {
    return await openDB<QAOfflineDB>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('inspections')) {
          const s = db.createObjectStore('inspections', { keyPath: 'id' }); s.createIndex('by-synced', 'lastSynced')
        }
        if (!db.objectStoreNames.contains('areas')) {
          const s = db.createObjectStore('areas', { keyPath: 'id' }); s.createIndex('by-inspection', 'inspectionId'); s.createIndex('by-synced', 'lastSynced')
        }
        if (!db.objectStoreNames.contains('evidence')) {
          const s = db.createObjectStore('evidence', { keyPath: 'id' }); s.createIndex('by-area', 'areaId'); s.createIndex('by-status', 'status'); s.createIndex('by-created', 'createdAt')
        }
        if (!db.objectStoreNames.contains('syncQueue')) {
          const s = db.createObjectStore('syncQueue', { keyPath: 'id' }); s.createIndex('by-status', 'status'); s.createIndex('by-created', 'createdAt'); s.createIndex('by-entity', 'entityType')
        }
        if (!db.objectStoreNames.contains('metadata')) {
          db.createObjectStore('metadata', { keyPath: 'key' })
        }
      },
      blocked() { console.warn('[QA Offline DB] Database upgrade blocked — close other tabs and reload') },
    })
  } catch (error: unknown) {
    const isRecoverable = error?.name === 'NotFoundError' || error?.name === 'DataError' ||
      (error?.message && /(object store|not a valid key)/i.test(error.message))
    if (isRecoverable && !isRetry) {
      console.warn('[QA Offline DB] Stale schema detected — deleting and recreating database')
      try { await deleteDB(DB_NAME) } catch { /* best-effort */ }
      dbPromise = null
      return openOfflineDB(true)
    }
    console.error('[QA Offline DB] Failed to open database:', error)
    dbPromise = null
    throw error
  }
}
